import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type {
  ProgrammeData,
  ProgrammeBlockCreate,
  ProgrammeBlockPatch,
  ProgrammeCategoryCreate,
  ProgrammeCategoryPatch
} from '../types'

// Programming Overview data hooks (spec 41). One query per year; mutations
// invalidate the year (and the shared categories list where relevant).

export function useProgramme(year: number) {
  return useQuery({
    queryKey: ['programme', year],
    queryFn: () => ApiClient.programme.get(year)
  })
}

export function useProgrammeCategories() {
  return useQuery({
    queryKey: ['programme-categories'],
    queryFn: ApiClient.programme.categories,
    staleTime: 60 * 60 * 1000
  })
}

export function useCreateBlocks(year: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ProgrammeBlockCreate) => ApiClient.programme.createBlocks(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['programme', year] })
  })
}

export function useUpdateBlock(year: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ProgrammeBlockPatch }) =>
      ApiClient.programme.updateBlock(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['programme', year] })
  })
}

export function useDeleteBlock(year: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => ApiClient.programme.deleteBlock(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['programme', year] })
  })
}

export function useUpdateBlockGroup(year: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { title: string; newTitle?: string; categoryId?: number }) =>
      ApiClient.programme.updateGroup({ year, ...body }),
    onSuccess: (data: ProgrammeData) => qc.setQueryData(['programme', year], data)
  })
}

export function useDeleteBlockGroup(year: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (title: string) => ApiClient.programme.deleteGroup({ year, title }),
    onSuccess: (data: ProgrammeData) => qc.setQueryData(['programme', year], data)
  })
}

export function useUpdateProgrammeYear(year: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { title?: string; subtitle?: string }) =>
      ApiClient.programme.updateYear(year, body),
    onSuccess: (data: ProgrammeData) => qc.setQueryData(['programme', year], data)
  })
}

export function useCreateCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ProgrammeCategoryCreate) => ApiClient.programme.createCategory(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['programme-categories'] })
      qc.invalidateQueries({ queryKey: ['programme'] })
    }
  })
}

export function useUpdateCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ProgrammeCategoryPatch }) =>
      ApiClient.programme.updateCategory(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['programme-categories'] })
      qc.invalidateQueries({ queryKey: ['programme'] })
    }
  })
}

export function useDeleteCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => ApiClient.programme.deleteCategory(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['programme-categories'] })
      qc.invalidateQueries({ queryKey: ['programme'] })
    }
  })
}

export function useImportProgramme(year: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: unknown) => ApiClient.programme.import(year, payload),
    onSuccess: (data: ProgrammeData) => {
      qc.setQueryData(['programme', year], data)
      qc.invalidateQueries({ queryKey: ['programme-categories'] })
    }
  })
}
